import React from 'react'
import { XMarkIcon, CalendarIcon, ClockIcon, MapPinIcon, UserIcon, UserGroupIcon, CheckCircleIcon, HandRaisedIcon, LinkIcon } from '@heroicons/react/24/outline'

interface ScheduledEvent {
  id: string
  time: string
  title: string
  description?: string
  location?: string
  eventUrl?: string
  coordinator?: string
  coordinatorName?: string
  isOptional?: boolean
  maxParticipants?: number | null
  deadline?: string | null
  rsvps?: Array<string | { userId: string; name: string; rsvpedAt: string }>
}

interface ScheduledEventDetailModalProps {
  isOpen: boolean
  onClose: () => void
  scheduledEvent: ScheduledEvent
  date: string
  currentUserId?: string
  onRsvp?: (attending: boolean) => void
  canEdit?: boolean
  isAdmin?: boolean
  onEdit?: () => void
  onDelete?: () => void
}

const ScheduledEventDetailModal: React.FC<ScheduledEventDetailModalProps> = ({
  isOpen,
  onClose,
  scheduledEvent,
  date,
  currentUserId,
  onRsvp,
  canEdit = false,
  isAdmin = false,
  onEdit,
  onDelete
}) => {
  const formatDateForDisplay = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatTime = (timeStr: string) => {
    const [hours, minutes] = timeStr.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}:${minutes} ${ampm}`
  }

  const isUserRSVPed = () => {
    if (!scheduledEvent.rsvps || !currentUserId) return false
    return scheduledEvent.rsvps.some(rsvp => 
      typeof rsvp === 'string' ? rsvp === currentUserId : rsvp.userId === currentUserId
    )
  }

  const getRSVPList = () => {
    if (!scheduledEvent.rsvps) return []
    return scheduledEvent.rsvps.map(rsvp => {
      if (typeof rsvp === 'string') {
        return { userId: rsvp, name: 'Unknown User', rsvpedAt: '' }
      }
      return rsvp
    })
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this activity? This action cannot be undone.')) {
      onDelete?.()
      onClose()
    }
  }

  if (!isOpen || !scheduledEvent) return null

  const rsvpList = getRSVPList()
  const isDeadlinePassed = scheduledEvent.deadline && new Date(scheduledEvent.deadline) < new Date()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <CalendarIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                {formatDateForDisplay(date)}
              </span>
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                • {formatTime(scheduledEvent.time)}
              </span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {scheduledEvent.title}
            </h2>
            <div className="flex items-center space-x-4">
              {scheduledEvent.isOptional && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  Optional
                </span>
              )}
              {scheduledEvent.deadline && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  isDeadlinePassed 
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                }`}>
                  {isDeadlinePassed ? 'Deadline Passed' : 'Has Deadline'}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {(canEdit || isAdmin) && onEdit && (
              <button
                onClick={onEdit}
                className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                title="Edit activity"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            {isAdmin && onDelete && (
              <button
                onClick={handleDelete}
                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                title="Delete activity"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          {scheduledEvent.description && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</h3>
              <p className="text-gray-600 dark:text-gray-400">{scheduledEvent.description}</p>
            </div>
          )}

          {/* Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scheduledEvent.location && (
              <div className="flex items-center space-x-2">
                <MapPinIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Location</div>
                  <div className="text-gray-600 dark:text-gray-400">{scheduledEvent.location}</div>
                </div>
              </div>
            )}

            {scheduledEvent.eventUrl && (
              <div className="flex items-center space-x-2">
                <LinkIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Website</div>
                  <a
                    href={scheduledEvent.eventUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline text-sm break-all"
                  >
                    Visit Website
                  </a>
                </div>
              </div>
            )}

            {scheduledEvent.coordinatorName && (
              <div className="flex items-center space-x-2">
                <UserIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Coordinator</div>
                  <div className="text-gray-600 dark:text-gray-400">{scheduledEvent.coordinatorName}</div>
                </div>
              </div>
            )}

            {scheduledEvent.maxParticipants && (
              <div className="flex items-center space-x-2">
                <UserGroupIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Max Participants</div>
                  <div className="text-gray-600 dark:text-gray-400">{scheduledEvent.maxParticipants}</div>
                </div>
              </div>
            )}

            {scheduledEvent.deadline && (
              <div className="flex items-center space-x-2">
                <ClockIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">RSVP Deadline</div>
                  <div className={`${isDeadlinePassed ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {new Date(scheduledEvent.deadline).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RSVP Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Attendees ({rsvpList.length})
              </h3>
              
              {onRsvp && !isDeadlinePassed && (
                <div className="flex items-center space-x-2">
                  {isUserRSVPed() ? (
                    <button
                      onClick={() => onRsvp(false)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800"
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                      Attending
                    </button>
                  ) : (
                    <button
                      onClick={() => onRsvp(true)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
                    >
                      <HandRaisedIcon className="w-4 h-4 mr-2" />
                      RSVP
                    </button>
                  )}
                </div>
              )}
            </div>

            {rsvpList.length > 0 ? (
              <div className="space-y-2">
                {rsvpList.map((rsvp, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                        <UserIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {rsvp.name}
                        </div>
                        {rsvp.rsvpedAt && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            RSVP'd {new Date(rsvp.rsvpedAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                    {rsvp.userId === currentUserId && (
                      <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                        You
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <UserGroupIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No one has RSVP'd yet</p>
                {onRsvp && !isDeadlinePassed && (
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Be the first to RSVP!
                  </p>
                )}
              </div>
            )}

            {isDeadlinePassed && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-300">
                  The RSVP deadline for this activity has passed.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-lg">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 hover:bg-gray-50 dark:hover:bg-gray-500 border border-gray-300 dark:border-gray-500 rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScheduledEventDetailModal
